import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { applyOrgFilter, getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

export type WarpConnection = {
  id: string;
  isActive: boolean;
  lastSyncedAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  workerCount: number;
  connectedAt: string;
  updatedAt: string;
};

export type WarpWorker = {
  id: string;
  warpWorkerId: string;
  type: string | null;
  status: string | null;
  position: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
  workEmail: string | null;
  isBusiness: boolean | null;
  businessName: string | null;
  startDate: string | null;
  endDate: string | null;
  departmentName: string | null;
  profileId: string | null;
  vendorId: string | null;
  syncedAt: string;
};

export type WarpSyncResult = {
  synced: number;
  contractors: number;
  employees: number;
  linkedToProfiles: number;
  profilesCreated: number;
  profilesLinkedByName: number;
  ambiguousSkipped: number;
};

export type WarpLinkResult = {
  created: number;
  linked: number;
};

/** Card-friendly status shape, matching the other integration services. */
export type WarpConnectionStatus = {
  isConnected: boolean;
  connectedAt: string | null;
  lastSyncedAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  workerCount: number;
};

function mapConnection(row: any): WarpConnection {
  return {
    id: row.id,
    isActive: row.is_active ?? false,
    lastSyncedAt: row.last_synced_at ?? null,
    lastSyncStatus: row.last_sync_status ?? null,
    lastSyncError: row.last_sync_error ?? null,
    workerCount: row.worker_count ?? 0,
    connectedAt: row.connected_at,
    updatedAt: row.updated_at,
  };
}

function mapWorker(row: any): WarpWorker {
  return {
    id: row.id,
    warpWorkerId: row.warp_worker_id,
    type: row.type ?? null,
    status: row.status ?? null,
    position: row.position ?? null,
    firstName: row.first_name ?? null,
    lastName: row.last_name ?? null,
    displayName: row.display_name ?? null,
    email: row.email ?? null,
    workEmail: row.work_email ?? null,
    isBusiness: row.is_business ?? null,
    businessName: row.business_name ?? null,
    startDate: row.start_date ?? null,
    endDate: row.end_date ?? null,
    departmentName: row.department_name ?? null,
    profileId: row.profile_id ?? null,
    vendorId: row.vendor_id ?? null,
    syncedAt: row.synced_at,
  };
}

async function invokeEdgeFunction(functionName: string, body: Record<string, unknown>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      apikey: SUPABASE_ANON_KEY,
    },
    // Pass userId in the body — auth.ts falls back to it when the gateway/getUser
    // can't resolve the user from the Bearer token (matches the other integrations).
    body: JSON.stringify({ userId: session?.user?.id, ...body }),
  });

  if (!res.ok) {
    const text = await res.text();
    let message = text || `Edge function error ${res.status}`;
    try {
      message = JSON.parse(text).error ?? message;
    } catch {
      // keep raw text
    }
    throw new Error(message);
  }

  return res.json();
}

const financeWarpService = {
  /** Validate + store the Warp API key for the active org. */
  async connect(apiKey: string): Promise<void> {
    await invokeEdgeFunction('warp-sync', {
      action: 'connect',
      apiKey,
      organizationId: getActiveOrgId(),
    });
  },

  async disconnect(): Promise<void> {
    await invokeEdgeFunction('warp-sync', {
      action: 'disconnect',
      organizationId: getActiveOrgId(),
    });
  },

  async getStatus(): Promise<WarpConnection | null> {
    const res = await invokeEdgeFunction('warp-sync', {
      action: 'getStatus',
      organizationId: getActiveOrgId(),
    });
    return res.connection ? mapConnection(res.connection) : null;
  },

  /** Card-friendly status (never throws to null-guard the UI). */
  async getConnectionStatus(): Promise<WarpConnectionStatus> {
    const conn = await financeWarpService.getStatus();
    return {
      isConnected: !!conn?.isActive,
      connectedAt: conn?.connectedAt ?? null,
      lastSyncedAt: conn?.lastSyncedAt ?? null,
      lastSyncStatus: conn?.lastSyncStatus ?? null,
      lastSyncError: conn?.lastSyncError ?? null,
      workerCount: conn?.workerCount ?? 0,
    };
  },

  /** Create/link fx_vendors for every synced contractor. */
  async linkToVendors(): Promise<WarpLinkResult> {
    return invokeEdgeFunction('warp-sync', {
      action: 'linkToVendor',
      organizationId: getActiveOrgId(),
    });
  },

  /**
   * Pull the roster from Warp into warp_workers. Pass `types: ['contractor']` to
   * sync contractors only, or `createMissingProfiles: true` to auto-create a
   * profile for any individual worker not already matched by personal/work email.
   */
  async syncWorkers(options?: {
    types?: string[];
    statuses?: string[];
    createMissingProfiles?: boolean;
  }): Promise<WarpSyncResult> {
    return invokeEdgeFunction('warp-sync', {
      action: 'syncWorkers',
      organizationId: getActiveOrgId(),
      types: options?.types,
      statuses: options?.statuses,
      createMissingProfiles: options?.createMissingProfiles,
    });
  },

  /** Read the synced roster from the local cache (no Warp call). */
  async getWorkers(options?: { type?: string }): Promise<WarpWorker[]> {
    let query = supabase
      .from('warp_workers')
      .select('*')
      .order('first_name', { ascending: true });

    if (options?.type) query = query.eq('type', options.type);
    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapWorker);
  },
};

export default financeWarpService;
